<template>
  <StudioMultiselect
    shouldShowSearchInput
    :isOpen="isOpen"
    :multiselectItems="visibleUserFilterItems"
    :isLoading="isLoading"
    :searchTerm="searchTerm"
    @onToggleSelectItem="handleToggleUserSelection"
    @onSearch="updateVisibleUserFilterItems"
    @close="isOpen = false"
  >
    <template #trigger>
      <StudioMultiselectTriggerButton
        :selectedItemsTitles="selectedUserNames"
        :suffix="{ singular: 'Created By', plural: 'Created By' }"
        :characterLengthThreshold="60"
        @open="isOpen = !isOpen"
        @clear="handleClearSelection"
      />
    </template>
  </StudioMultiselect>
</template>

<script setup lang="ts">
import { listUser, User } from '@goldcast/api/backend';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';

import StudioMultiselect, { MultiselectItem } from '@/studioComponents/StudioMultiselect.vue';
import StudioMultiselectTriggerButton from '@/studioComponents/StudioMultiselectTriggerButton.vue';

const props = defineProps<{
  selectedUserIds: string[];
}>();

const emit = defineEmits<{
  (e: 'update:selectedUserIds', userIds: string[]): void;
}>();

const store = useStore();
const isOpen = ref(false);
const isLoading = ref(false);
const userFilterItems = ref<MultiselectItem[]>([]);
const visibleUserFilterItems = ref<MultiselectItem[]>([]);
const searchTerm = ref('');

const selectedUserNames = computed(() => {
  return userFilterItems.value.filter(item => item.selected).map(item => item.title);
});

watch(
  () => props.selectedUserIds,
  newSelectedUserIds => {
    userFilterItems.value = userFilterItems.value.map(item => ({
      ...item,
      selected: newSelectedUserIds.includes(item.id)
    }));
  },
  { deep: true }
);

onMounted(() => {
  fetchOrgUserList();
});

const fetchOrgUserList = async () => {
  isLoading.value = true;
  try {
    const org = store.getters['org/getCurrentOrg'];
    const organizationUsers = (await listUser({
      queryParams: {
        organization: org.id,
        workspaces: 'all',
        exclude_goldcastevents_users: true
      }
    })) as User[];

    if (organizationUsers) {
      userFilterItems.value = organizationUsers.map(user => ({
        id: user.id,
        title: user.first_name + ' ' + user.last_name,
        selected: props.selectedUserIds.includes(user.id)
      }));
      visibleUserFilterItems.value = userFilterItems.value;
    }
  } catch (error) {
    console.error('Error fetching organization users:', error);
  } finally {
    isLoading.value = false;
  }
};

const updateVisibleUserFilterItems = (searchTerm: string) => {
  if (!searchTerm) {
    visibleUserFilterItems.value = userFilterItems.value;
    return;
  }

  visibleUserFilterItems.value = userFilterItems.value.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

const handleToggleUserSelection = (userId: string) => {
  userFilterItems.value = userFilterItems.value.map(item => ({
    ...item,
    selected: item.id === userId ? !item.selected : item.selected
  }));

  const selectedIds = userFilterItems.value.filter(item => item.selected).map(item => item.id);

  emit('update:selectedUserIds', selectedIds);
};

const handleClearSelection = () => {
  userFilterItems.value = userFilterItems.value.map(item => ({
    ...item,
    selected: false
  }));
  isOpen.value = false;
  emit('update:selectedUserIds', []);
};
</script>
